import { Search } from "lucide-react";
import { useDeferredValue, useEffect, useRef, useState } from "react";
import { api } from "../api";
import { EmptyState, LoadingState, PageHeader, Pagination, formatDate } from "../components";
import { useAppData } from "../context";
import type { AuditEvent } from "../types";

const PAGE_SIZE = 50;

export function Audit() {
  const { data } = useAppData();
  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query);
  const [type, setType] = useState("ALL");
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const requestRef = useRef<AbortController | null>(null);

  useEffect(() => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
    if (deferredQuery.trim()) params.set("query", deferredQuery.trim());
    if (type !== "ALL") params.set("objectType", type);
    setLoading(true);
    void api.auditEvents(params, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setEvents(result.items);
        setTotal(result.total);
        setError("");
      })
      .catch((cause: unknown) => {
        if (!controller.signal.aborted) setError(cause instanceof Error ? cause.message : "审计记录加载失败。");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
        if (requestRef.current === controller) requestRef.current = null;
      });
    return () => controller.abort();
  }, [deferredQuery, page, type]);

  if (!data || (loading && !events.length)) return <LoadingState />;
  return <>
    <PageHeader eyebrow="AUDIT / IMMUTABLE LOG" title="操作审计" />
    {error ? <div className="inline-message" role="alert">{error}</div> : null}
    <section className="workspace-band">
      <div className="filter-bar">
        <label className="search-field"><Search size={16} /><input aria-label="搜索审计" value={query} onChange={(event) => { setPage(1); setQuery(event.target.value); }} placeholder="操作者、动作、对象" /></label>
        <select aria-label="对象类型" value={type} onChange={(event) => { setPage(1); setType(event.target.value); }}><option value="ALL">全部对象</option><option value="TASK">任务</option><option value="ACCOUNT">账号</option><option value="PRECHECK">预检</option><option value="PRODUCT_MAPPING">商品映射</option><option value="ORDER">订单同步</option><option value="ORDER_EXCEPTION">异常订单</option><option value="NOTIFICATION">提醒</option><option value="SETTINGS">设置</option><option value="EXPORT">导出</option><option value="AUTH">授权</option><option value="WEBHOOK">Webhook</option><option value="AI_CONTENT">AI 内容</option></select>
        <span className="filter-count">{total} 条记录</span>
      </div>
      {events.length ? <><div className="table-scroll"><table className="data-table audit-table"><thead><tr><th>时间</th><th>操作者</th><th>角色</th><th>动作</th><th>对象</th><th>摘要</th><th>来源 IP</th></tr></thead><tbody>{events.map((event) => <tr key={event.id}><td>{formatDate(event.at)}</td><td><strong>{event.actor}</strong></td><td>{event.role}</td><td><span className="mono action-code">{event.action}</span></td><td><strong>{event.objectType}</strong><small className="mono">{event.objectId.slice(0, 20)}</small></td><td>{event.summary}</td><td className="mono">{event.ip}</td></tr>)}</tbody></table></div><Pagination page={page} total={total} pageSize={PAGE_SIZE} onChange={setPage} /></> : <EmptyState icon="audit" title="没有符合条件的审计记录" />}
    </section>
  </>;
}
